import { useState } from 'react'

import { SplitsLink } from '~/components/Link'
import { PartialNetworkPill } from '~/components/PartialNetworkPill'
import {
  Group,
  List,
  NetworkDisplay,
  Toggle,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { DOCS_URL } from '~/constants/externalUrls'
import { useAllChainIds } from '~/hooks/utils'
import { areSetsEquals, sortChainIds } from '~/utils'

const NETWORKS_GUIDE_URL = `${DOCS_URL}/introduction/networks-and-assets`

type ChangeNetworksDialogProps = {
  networks: number[]
  onSave: (networks: number[]) => void
  onClose: () => void
  isSaving?: boolean
  canSave?: boolean
  requireOne?: boolean
}

export const ChangeNetworksDialog = ({
  networks,
  onSave,
  onClose,
  isSaving = false,
  canSave = true,
  requireOne = false,
}: ChangeNetworksDialogProps) => {
  const [selectedNetworks, setSelectedNetworks] = useState(() =>
    sortChainIds(networks),
  )
  const hasChanges = !areSetsEquals(
    new Set(networks),
    new Set(selectedNetworks),
  )

  // A pre-enabled network with no row here is a network the user cannot turn
  // off, so the picker lists every one rather than full-tier ones only. Each
  // carries the same caveat as in workspace settings.
  const smartAccountChainIds = sortChainIds(useAllChainIds())

  const save = () => onSave(selectedNetworks)

  return (
    <SplitsDialogContent
      id="change-networks-dialog"
      title="Active networks"
      variant="cta"
      isLoading={isSaving}
      actionButton={{
        text: 'Save',
        onClick: save,
        isDisabled:
          !canSave ||
          !hasChanges ||
          (requireOne && selectedNetworks.length === 0),
        type: 'submit',
      }}
      cancelButton={{
        text: 'Cancel',
        onClick: onClose,
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
        className="space-y-4"
      >
        <Group>
          Your workspace&apos;s accounts will be synced across these networks.
          These can be changed later.
        </Group>
        <Group label="Networks">
          <List
            items={smartAccountChainIds.map((chainId) => ({
              key: `${chainId}`,
              content: (
                <div className="flex items-center space-x-2">
                  <NetworkDisplay layout="standard" chainId={chainId} />
                  <PartialNetworkPill chainId={chainId} />
                </div>
              ),
              onClick: () =>
                setSelectedNetworks((current) =>
                  current.includes(chainId)
                    ? current.filter((id) => id !== chainId)
                    : [...current, chainId],
                ),
              actionIcon: (
                <Toggle
                  isChecked={selectedNetworks.includes(chainId)}
                  size="base"
                />
              ),
            }))}
            footer={
              <SplitsLink path={NETWORKS_GUIDE_URL} target="tab" underline>
                Learn how networks work
              </SplitsLink>
            }
          />
        </Group>
      </form>
    </SplitsDialogContent>
  )
}
